import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { EventsTableHeaderStyle } from 'src/domains/sportsbook/webview/components/eventsTable/EventsMainSectionWrapper';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

export const EventsNFLLogoImageWrapper = withConfig(theme => styled('div', { label: 'EventsNFLLogoImageWrapper' })`
    background: ${theme.star.eventsTableColors.bgColorSecondary};
    display: flex;
    height: 72px;
    justify-content: center;
    padding: 16px 0;
`);

export const EventsNFLLogoImage = styled('img', { label: 'EventsNFLLogoImage' })`
    width: 157px;
`;

const setEventTableHeader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme, headerStyle: EventsTableHeaderStyle): SerializedStyles => {
    switch (headerStyle) {
        case EventsTableHeaderStyle.SOLID:
            return css`
                background-color: ${theme.star.eventsTableColors.bgColor};
                padding: ${variant === DefaultComponentVariantType.SECONDARY ? '0' : '0 0 6px 0'};
            `;
        case EventsTableHeaderStyle.DOUBLE:
            return css`
                padding: ${variant === DefaultComponentVariantType.SECONDARY ? '0' : '0 0 14px 0'};
            `;
        default:
            return css``;
    }
};

const setEventTableHeaderVariant = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            margin: 8px 0 0;
            @media ${theme.star.mediaQuery.tablet} {
                border-radius: ${theme.star.radius.medium} ${theme.star.radius.medium} 0 0;
                margin: 8px 8px 0;
                overflow: hidden;
            }
        `;
    }
    return css``;
};

interface EventsTableHeaderType {
    headerStyle: EventsTableHeaderStyle;
    variant: DefaultComponentVariantType | null;
}

export const EventsTableHeader = withConfig(theme => styled('header', { label: 'EventsTableHeader' })<EventsTableHeaderType>`
    ${({ variant }): SerializedStyles => setEventTableHeaderVariant(variant, theme)}
    ${({ headerStyle, variant }): SerializedStyles => setEventTableHeader(variant, theme, headerStyle)}
`);

const setFilterStyle = (theme: EmotionTheme, headerStyle: EventsTableHeaderStyle, isCompetitionPage?: boolean): SerializedStyles => {
    switch (headerStyle) {
        case EventsTableHeaderStyle.SOLID: {
            if (isCompetitionPage === true) {
                return css`
                    background-color: ${theme.star.eventsTableColors.bgColor};
                `;
            }
            return css`
                background-color: ${theme.star.filtersColors.wrapper.bgColorQuaternary};
            `;
        }
        case EventsTableHeaderStyle.DOUBLE: 
            return css`
                padding: 0 0 14px 0;
            `;
        default:
            return css``;
    }
};

interface FiltersStyleType {
    headerStyle: EventsTableHeaderStyle;
    variant: DefaultComponentVariantType | null;
    isCompetitionPage?: boolean;
}

const setAdditionalFilterStyle = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${theme.star.eventsTableColors.bgColor};
            @media ${theme.star.mediaQuery.tablet} {
                margin: 0 8px;
            }
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            background-color: ${theme.star.unified.filtersColors.tertiary.bgWrapperColor};
            padding: 0 ${theme.star.padding.xMedium} ${theme.star.padding.xMedium};
            
            & [class$='FiltersTertiaryList'] {
                padding: 0;
            }
        `;
    }
    return css``;
};

export const FiltersStyle = withConfig(theme => styled('div', { label: 'FiltersStyle' })<FiltersStyleType>`
    ${({ headerStyle, isCompetitionPage }): SerializedStyles => setFilterStyle(theme, headerStyle, isCompetitionPage)};
    padding: ${theme.star.padding.small} ${theme.star.padding.medium} ${theme.star.padding.medium};
    ${({ variant }): SerializedStyles => setAdditionalFilterStyle(variant, theme)}
`);

const setHeading = (headerStyle: EventsTableHeaderStyle, variant: DefaultComponentVariantType | null): SerializedStyles => {
    switch (headerStyle) {
        case EventsTableHeaderStyle.SOLID:
            return css`
                padding: 9px 10px 9px 10px;
                margin-bottom: ${variant === DefaultComponentVariantType.SECONDARY ? '0' : '4px'};
            `;
        case EventsTableHeaderStyle.DOUBLE:
            return css`
                padding: 10px;
                margin-bottom: ${variant === DefaultComponentVariantType.SECONDARY ? '0' : '13px'};
            `;
        default:
            return css``;
    }
};

interface HeadingType {
    headerStyle: EventsTableHeaderStyle;
    variant: DefaultComponentVariantType | null;
}

export const Heading = withConfig(theme => styled('div', { label: 'Heading' })<HeadingType>` 
    ${({ headerStyle, variant }): SerializedStyles => setHeading(headerStyle, variant)};
    text-transform: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? 'capitalize' : 'uppercase'};
    background-color: ${theme.star.eventsTableColors.bgColor};
    color: ${theme.star.eventsTableColors.txtColor};
    display: flex;
    align-content: center;
    font-size: ${theme.star.fontSize.xRegular};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.7143;
`);

export const HeadingTitle = styled('span', { label: 'HeadingTitle' })`
    margin-left: 8px;
`;
